/* eslint-disable @typescript-eslint/no-explicit-any */

import type {
  GlobalSearchShellExtension,
  GlobalSearchAction,
  GlobalSearchCategory,
  GlobalSearchCategoryColumn,
  GlobalSearchValueField,
} from '../global-search-extension'
import type { Settings } from '../settings-definition'
import type { ShellExtension, ShellExtensionCustomElements, ShellExtensionQuickActions } from '../shell-extension'

/**
 * Validates that the object passed is a shell extension
 * @param extension Object to validate
 * @returns true if object is an extension, false otherwise
 */
export const isShellExtension = (extension: any): extension is ShellExtension => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: ShellExtension = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
  }

  return isSchemaSupported(extension, schema)
}

const isPropertyOfType = (property: any, types: readonly string[]): boolean => types.includes(typeof property)

const isSchemaSupported = <T extends object>(object: any, schema: T): object is T => {
  const keys = Object.keys(schema) as unknown as readonly (keyof T)[]
  return !keys.some(key => typeof object?.[key] !== typeof schema[key])
}

/**
 * Validates if an extension support custom elements interface
 * @param extension extension to validate
 * @returns true if extension support custom elements interface, false otherwise
 */
export const supportsCustomElements = (extension: ShellExtension): extension is ShellExtensionCustomElements => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: ShellExtensionCustomElements = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
    registerCustomElements: () => {},
  }
  return isSchemaSupported(extension, schema)
}

/**
 * Validates if an extension support quick actions interface
 * @param extension extension to validate
 * @returns true if extension support quick actions interface, false otherwise
 */
export const supportsQuickActions = (extension: ShellExtension): extension is ShellExtensionQuickActions => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: ShellExtensionQuickActions = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
    getQuickActions: () => [],
  }
  return isSchemaSupported(extension, schema)
}

/**
 * Check Settings type
 * @param settings
 * @returns
 */
export const isSettings = (settings: any): settings is Settings => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schemaSettings: Settings = {
    displayNameKey: '',
    componentId: '',
    route: '',
  }

  const keys = Object.keys(schemaSettings) as readonly (keyof Settings)[]
  return !keys.some(key => typeof settings?.[key] !== typeof schemaSettings[key])
}

/**
 * Validates if an extension support global search interface
 * @param extension extension to validate
 * @returns true if extension support global search interface, false otherwise
 */
export const supportsGlobalSearch = (extension: ShellExtension): extension is GlobalSearchShellExtension => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: GlobalSearchShellExtension = {
    id: '',
    initialize: async () => {},
    registerNamespaces: async () => {},
    getString: () => '',
    getComponentById: async () => null,
    getModules: () => [],
    getSettings: () => [],
    getNavigationItems: () => [],
    getMetadata: () => ({ id: '', name: '', version: '' }),
    getGlobalSearchCategories: () => [],
    getGlobalSearchResultCategoryId: () => '',
    getGlobalSearchResultActions: () => [],
    getGlobalSearchResultDefaultAction: () => undefined,
    getActionsForSearchString: () => [],
  }
  return isSchemaSupported(extension, schema)
}

/**
 * Validate if an object is a global search action
 * @param object object to validate
 * @returns true if object is a search action, false otherwise
 */
export const isGlobalSearchAction = (object: any): object is GlobalSearchAction => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: GlobalSearchAction = {
    id: '',
    command: {
      commandName: '',
      namespace: '',
    },
    displayNameKey: '',
    icon: '',
  }
  return isSchemaSupported(object, schema) && isSchemaSupported(object.command, schema.command)
}

/**
 * Validate if an object is a global search category column
 * @param object object to validate
 * @returns true if object is a search category column, false otherwise
 */
export const isGlobalSearchCategoryColumn = (object: any): object is GlobalSearchCategoryColumn => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: Partial<GlobalSearchCategoryColumn> = {
    displayNameKey: '',
    id: '',
  }
  return isSchemaSupported(object, schema) && isGlobalSearchValueField(object.valueField)
}

/**
 * Validate if a property is a global search value field
 * @param property property to validate
 * @returns true if property is a string or a function, false otherwise.
 */
export const isGlobalSearchValueField = (property: any): property is GlobalSearchValueField =>
  isPropertyOfType(property, ['string', 'function'])

/**
 * Validate if an object is a global search category
 * @param object object to validate
 * @returns true if object is a search category, false otherwise
 */
export const isGlobalSearchCategory = (object: any): object is GlobalSearchCategory => {
  /* istanbul ignore next -> this is only a schema object so the functions are never called */
  const schema: Partial<GlobalSearchCategory> = {
    id: '',
    columns: [],
  }

  return (
    isSchemaSupported(object, schema) &&
    isGlobalSearchValueField(object.previewField) &&
    isGlobalSearchValueField(object.titleField) &&
    (isGlobalSearchValueField(object.userKeyField) || isGlobalSearchValueField(object.iconField)) &&
    !object.columns?.some(column => !isGlobalSearchCategoryColumn(column))
  )
}
